import { cn } from "@/lib/utils";

/** Base shimmer block used across the admin console. */
export function Shimmer({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md bg-muted/60",
        "after:absolute after:inset-0 after:-translate-x-full after:animate-[shimmer_1.4s_infinite]",
        "after:bg-gradient-to-r after:from-transparent after:via-foreground/10 after:to-transparent",
        className,
      )}
    />
  );
}

/** Light table shimmer shown instead of a blocking page spinner. */
export function TableSkeleton({ rows = 10, cols = 9 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-border/60">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-3 py-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Shimmer
              key={c}
              className={cn("h-3.5 flex-1", c === 0 && "flex-[1.6]", c > 5 && "hidden md:block")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** KPI + panel shimmer for the admin dashboard first paint. */
export function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-4">
            <Shimmer className="h-3 w-24" />
            <Shimmer className="mt-3 h-7 w-16" />
            <Shimmer className="mt-3 h-3 w-32" />
          </div>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-[7fr_3fr]">
        <div className="rounded-2xl border border-border bg-card p-4">
          <Shimmer className="h-4 w-40" />
          <div className="mt-4">
            <TableSkeleton rows={6} cols={5} />
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <Shimmer className="h-4 w-32" />
          <div className="mt-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Shimmer key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
